import { type VariantProps } from "class-variance-authority";
import {
  Check,
  ChevronsUpDown,
  Loader2,
  X,
  type LucideIcon,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import {
  asyncSelectChipsVariants,
  asyncSelectContentVariants,
  asyncSelectTriggerVariants,
} from "./async-select-variants";
import { Button } from "./button";
import { Checkbox } from "./checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./command";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

import { Badge } from "@/components/ui/v2/badge";
import { buttonVariants } from "@/components/ui/v2/button-variants";
import { useDebounce } from "@/lib/hooks/useDebounce";
import { cn } from "@/lib/utils/cn";

export interface Option {
  value: string;
  label: string;
  disabled?: boolean;
  description?: string;
  icon?: React.ReactNode;
}

/** Result shape for paginated fetcher (load more on scroll) */
export interface AsyncSelectPaginatedResult<T> {
  items: T[];
  totalCount: number;
}

export type AsyncSelectSelectionControls<T> = {
  fetchMatches: (
    query: string,
    pageSize: number,
  ) => Promise<AsyncSelectPaginatedResult<T>>;
};

interface AsyncSelectBaseProps<T> {
  /** Async function to fetch options (non-paginated) */
  fetcher?: (query?: string) => Promise<T[]>;
  /** Paginated fetcher for load-more-on-scroll: (query?, page) => { items, totalCount } */
  paginatedFetcher?: (
    query?: string,
    page?: number,
  ) => Promise<AsyncSelectPaginatedResult<T>>;
  /** Preload all data ahead of time (only when using fetcher) */
  preload?: boolean;
  /** Function to filter options (only when using fetcher + preload) */
  filterFn?: (option: T, query: string) => boolean;
  /** Function to render each option */
  renderOption: (option: T) => React.ReactNode;
  /** Function to get the value from an option */
  getOptionValue: (option: T) => string;
  /** Function to get the display value for the selected option */
  getDisplayValue: (option: T) => React.ReactNode;
  /** Custom not found message */
  notFound?: React.ReactNode;
  /** Custom loading skeleton */
  loadingSkeleton?: React.ReactNode;
  /** Label for the select field */
  label?: string;
  /** Placeholder text when no selection */
  placeholder?: string;
  /** Disable the entire select */
  disabled?: boolean;
  /** Custom width for the popover */
  width?: string | number;
  /** Custom class names */
  className?: string;
  /** Custom trigger button class names */
  triggerClassName?: string;
  triggerIcon?: LucideIcon;
  triggerVariant?: VariantProps<typeof asyncSelectTriggerVariants>["variant"];
  contentWidth?: VariantProps<typeof asyncSelectContentVariants>["width"];
  /** Custom no results message */
  noResultsMessage?: string;
  /** Allow clearing the selection */
  clearable?: boolean;
  /** Option values that should be shown but not selectable (greyed out) */
  disabledOptions?: string[];
  /** Whether the field has an error (for destructive styling) */
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
  /** Aria label for the multi-select clear-all control */
  clearAriaLabel?: string;
  /**
   * Render the raw value in the single-select trigger when its option has not loaded yet, instead of the
   * placeholder. Only set this where the value is itself human-readable — a select over opaque ids would
   * show a bare GUID.
   */
  fallbackToRawValue?: boolean;
  forceOpen?: boolean;
  contentRef?: React.Ref<HTMLDivElement>;
}

/**
 * Single-select (default): `value`/`onChange` are strings, the popover closes on select.
 * Multi-select (`multiple`): `value`/`onChange` are string arrays, selections render as chips, and
 * the popover stays open on toggle.
 */
export type AsyncSelectProps<T> = AsyncSelectBaseProps<T> &
  (
    | {
        multiple?: false;
        value: string;
        onChange: (value: string) => void;
        selectionControls?: never;
      }
    | {
        multiple: true;
        value: string[];
        onChange: (value: string[]) => void;
        selectionControls?: AsyncSelectSelectionControls<T>;
      }
  );

const LOAD_MORE_SCROLL_THRESHOLD = 60;

export function AsyncSelect<T>({
  fetcher,
  paginatedFetcher,
  preload,
  filterFn,
  renderOption,
  getOptionValue,
  getDisplayValue,
  notFound,
  loadingSkeleton,
  label,
  placeholder,
  value,
  onChange,
  disabled = false,
  width = "200px",
  className,
  triggerClassName,
  triggerIcon: TriggerIcon = ChevronsUpDown,
  triggerVariant,
  contentWidth,
  noResultsMessage,
  clearable = true,
  disabledOptions,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedBy,
  multiple = false,
  clearAriaLabel,
  fallbackToRawValue = false,
  selectionControls,
  forceOpen = false,
  contentRef,
}: AsyncSelectProps<T>) {
  const { t } = useTranslation();
  const isPaginated = Boolean(paginatedFetcher);
  const optionsId = useId();
  const [isOpen, setOpen] = useState(false);
  const open = forceOpen || isOpen;
  const [fetchedOptions, setFetchedOptions] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedValue, setSelectedValue] = useState(value);
  const [selectedOption, setSelectedOption] = useState<T | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [exhausted, setExhausted] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [clearFailed, setClearFailed] = useState(false);
  const listScrollRef = useRef<HTMLDivElement>(null);
  const resolvedSelectionRef = useRef<string | null>(null);
  const preloadedFetcherRef = useRef<AsyncSelectProps<T>["fetcher"]>(undefined);
  const debouncedSearchTerm = useDebounce(searchTerm, preload ? 0 : 300);

  const resolvedPlaceholder = placeholder ?? t("placeholders.selectDefault");
  const resolvedClearAriaLabel = clearAriaLabel ?? t("common.clearSelection");

  const toErrorMessage = useCallback(
    (err: unknown): string =>
      err instanceof Error ? err.message : t("errors.fetchOptionsFailed"),
    [t],
  );

  // Multi-select drives off the array value; single-select keeps using selectedValue/selectedOption.
  const selectedValues: string[] = useMemo(
    () => (Array.isArray(value) ? value : value ? [value] : []),
    [value],
  );

  // Identity-stable stand-in for `selectedValues`: callers rebuild that array on every render, so the
  // array itself cannot gate an effect without re-running it on every render.
  const selectedValuesKey = selectedValues.join("\u0000");

  const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);

  const options: T[] = useMemo(() => {
    if (isPaginated || !preload || !debouncedSearchTerm) return fetchedOptions;
    return fetchedOptions.filter((option) =>
      filterFn ? filterFn(option, debouncedSearchTerm) : true,
    );
  }, [isPaginated, preload, debouncedSearchTerm, fetchedOptions, filterFn]);

  const hasMore = !exhausted && fetchedOptions.length < totalCount;
  const isSearchLoading = loading && options.length > 0;

  const isTermUnsettled = searchTerm !== debouncedSearchTerm;
  const clearAllDisabled =
    isClearing ||
    isTermUnsettled ||
    selectedValues.length === 0 ||
    (debouncedSearchTerm !== "" && (loading || totalCount === 0));

  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  // Initialize selectedOption when options are loaded and value exists
  useEffect(() => {
    if (value && options.length > 0) {
      const option = options.find((opt) => getOptionValue(opt) === value);
      if (option) {
        setSelectedOption(option);
      }
    }
  }, [value, options, getOptionValue]);

  // Preload fetches the whole option set once and filters it in memory. Gate on the fetcher itself, not
  // just the effect deps, so a consumer that rebuilds its fetcher on every render re-filters rather than
  // re-requesting; a genuinely new fetcher (different client, different module) still reloads.
  useEffect(() => {
    if (isPaginated || !fetcher || !preload) return;
    if (preloadedFetcherRef.current === fetcher) return;
    preloadedFetcherRef.current = fetcher;
    let cancelled = false;
    const loadAll = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetcher();
        if (!cancelled) setFetchedOptions(data);
      } catch (err) {
        if (!cancelled) setError(toErrorMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadAll();
    return () => {
      cancelled = true;
    };
  }, [isPaginated, fetcher, preload, toErrorMessage]);

  useEffect(() => {
    if (isPaginated || !fetcher || preload) return;
    let cancelled = false;
    const loadForQuery = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetcher(debouncedSearchTerm);
        if (!cancelled) setFetchedOptions(data);
      } catch (err) {
        if (!cancelled) setError(toErrorMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadForQuery();
    return () => {
      cancelled = true;
    };
  }, [isPaginated, fetcher, preload, debouncedSearchTerm, toErrorMessage]);

  // Paginated: fetch when popover opens and when search term changes (reset to page 1)
  useEffect(() => {
    if (!isPaginated || !paginatedFetcher || !open) return;
    let cancelled = false;
    setPage(1);
    setExhausted(false);
    const fetchFirstPage = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await paginatedFetcher(
          debouncedSearchTerm || undefined,
          1,
        );
        if (!cancelled) {
          setFetchedOptions(result.items);
          setTotalCount(result.totalCount);
          // Scroll list to top after React commits so user sees the start of the new results
          setTimeout(() => listScrollRef.current?.scrollTo({ top: 0 }), 0);
        }
      } catch (err) {
        if (!cancelled) {
          setError(toErrorMessage(err));
          setFetchedOptions([]);
          setTotalCount(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchFirstPage();
    return () => {
      cancelled = true;
    };
  }, [
    isPaginated,
    paginatedFetcher,
    debouncedSearchTerm,
    open,
    toErrorMessage,
  ]);

  // Paginated + an already-selected value: the fetch above is gated on `open`, so a value saved earlier
  // has no loaded option to resolve its label from until the user clicks the popover. Resolve page 1 once
  // per distinct selection — the ref, not `fetchedOptions.length`, is what stops a fetcher that keeps
  // returning nothing from re-firing on every parent render.
  useEffect(() => {
    if (!isPaginated || !paginatedFetcher || open) return;
    if (!selectedValuesKey || fetchedOptions.length > 0) return;
    if (resolvedSelectionRef.current === selectedValuesKey) return;
    resolvedSelectionRef.current = selectedValuesKey;

    let cancelled = false;
    const resolveSelectedOptions = async () => {
      const result = await paginatedFetcher(undefined, 1).catch(() => null);
      if (cancelled || !result) return;
      setFetchedOptions(result.items);
      setTotalCount(result.totalCount);
    };
    resolveSelectedOptions();
    return () => {
      cancelled = true;
    };
  }, [
    isPaginated,
    paginatedFetcher,
    open,
    selectedValuesKey,
    fetchedOptions.length,
  ]);

  const loadMore = useCallback(async () => {
    if (!paginatedFetcher || loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const result = await paginatedFetcher(
        debouncedSearchTerm || undefined,
        nextPage,
      );
      setFetchedOptions((prev) => [...prev, ...result.items]);
      setTotalCount(result.totalCount);
      setPage(nextPage);
      if (result.items.length === 0) setExhausted(true);
    } catch {
      setExhausted(true);
    } finally {
      setLoadingMore(false);
    }
  }, [paginatedFetcher, loadingMore, hasMore, page, debouncedSearchTerm]);

  const handleListScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (!isPaginated || !hasMore || loadingMore) return;
      const el = e.currentTarget;
      const nearBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight <
        LOAD_MORE_SCROLL_THRESHOLD;
      if (nearBottom) loadMore();
    },
    [isPaginated, hasMore, loadingMore, loadMore],
  );

  // Chip label from a loaded option, falling back to the raw value so a selected value that isn't
  // in the current (filtered/paged) option list still renders and stays clearable.
  const labelForValue = (v: string): React.ReactNode => {
    const option = options.find((opt) => getOptionValue(opt) === v);
    return option ? getDisplayValue(option) : v;
  };

  const handleSelect = useCallback(
    (currentValue: string) => {
      if (multiple) {
        const next = selectedValues.includes(currentValue)
          ? selectedValues.filter((v) => v !== currentValue)
          : [...selectedValues, currentValue];
        (onChange as (value: string[]) => void)(next);
        return; // keep the popover open for further toggling
      }
      const newValue =
        clearable && currentValue === selectedValue ? "" : currentValue;
      setSelectedValue(newValue);
      setSelectedOption(
        options.find((option) => getOptionValue(option) === newValue) || null,
      );
      (onChange as (value: string) => void)(newValue);
      setOpen(false);
    },

    [
      multiple,
      selectedValues,
      selectedValue,
      onChange,
      clearable,
      options,
      getOptionValue,
    ],
  );

  const changeValues = onChange as (value: string[]) => void;

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    setClearFailed(false);
  };

  const fetchAllMatches = async (
    fetchMatches: AsyncSelectSelectionControls<T>["fetchMatches"],
  ) => {
    const first = await fetchMatches(debouncedSearchTerm, totalCount);
    if (first.items.length === first.totalCount) return first.items;
    const retry = await fetchMatches(debouncedSearchTerm, first.totalCount);
    return retry.items.length === retry.totalCount ? retry.items : null;
  };

  const handleClearAll = async () => {
    if (!debouncedSearchTerm) {
      changeValues([]);
      return;
    }
    if (!selectionControls || isClearing) return;
    setIsClearing(true);
    setClearFailed(false);
    try {
      const matches = await fetchAllMatches(selectionControls.fetchMatches);
      if (!matches) {
        setClearFailed(true);
        return;
      }
      const matched = new Set(matches.map(getOptionValue));
      changeValues(selectedValues.filter((selected) => !matched.has(selected)));
    } catch {
      setClearFailed(true);
    } finally {
      setIsClearing(false);
    }
  };

  // Shared option list — rendered inside either the paginated scroll wrapper or a plain CommandList,
  // so the item/check markup lives in one place (both single- and multi-select paths).
  const optionListBody = (
    <>
      {error && <div className="p-4 text-destructive text-center">{error}</div>}
      {loading &&
        options.length === 0 &&
        (loadingSkeleton || <DefaultLoadingSkeleton />)}
      {!loading &&
        !error &&
        options.length === 0 &&
        (notFound || (
          <CommandEmpty>
            {noResultsMessage ??
              (label
                ? t("common.noEntityFound", { entity: label.toLowerCase() })
                : t("common.noResultsFound"))}
          </CommandEmpty>
        ))}
      <CommandGroup>
        {options.map((option) => {
          const optionValue = getOptionValue(option);
          const isDisabled = disabledOptions?.includes(optionValue);
          const isSelected = multiple
            ? selectedSet.has(optionValue)
            : selectedValue === optionValue;
          return (
            <CommandItem
              key={optionValue}
              value={optionValue}
              onSelect={() => handleSelect(optionValue)}
              disabled={isDisabled || isClearing}
              aria-checked={selectionControls ? isSelected : undefined}
            >
              {selectionControls && (
                <Checkbox
                  checked={isSelected}
                  tabIndex={-1}
                  aria-hidden
                  className="pointer-events-none"
                />
              )}
              {renderOption(option)}
              {!selectionControls && (
                <Check
                  className={cn(
                    "ml-auto h-3 w-3",
                    isSelected ? "opacity-100" : "opacity-0",
                  )}
                />
              )}
            </CommandItem>
          );
        })}
      </CommandGroup>
    </>
  );

  const selectionSummary = () => {
    if (selectedValues.length === 0) return resolvedPlaceholder;
    if (selectedValues.length === 1) return labelForValue(selectedValues[0]!);
    return t("common.selectedCount", { count: selectedValues.length });
  };

  const renderTriggerContent = () => {
    if (selectionControls) {
      return (
        <span
          className={cn(
            "min-w-0 flex-1 truncate",
            selectedValues.length === 0 && "text-muted-foreground",
          )}
        >
          {selectionSummary()}
        </span>
      );
    }
    if (multiple) {
      return (
        <span className={asyncSelectChipsVariants()}>
          {selectedValues.length === 0 ? (
            <span className="text-muted-foreground">{resolvedPlaceholder}</span>
          ) : (
            selectedValues.map((v) => (
              <Badge key={v} variant="secondary" size="small">
                {labelForValue(v)}
              </Badge>
            ))
          )}
        </span>
      );
    }
    return (
      <span className="min-w-0 flex-1 truncate">
        {selectedOption
          ? getDisplayValue(selectedOption)
          : (fallbackToRawValue && selectedValue) || resolvedPlaceholder}
      </span>
    );
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-controls={optionsId}
          aria-invalid={ariaInvalid}
          aria-describedby={ariaDescribedBy}
          data-slot="async-select-trigger"
          data-variant={triggerVariant ?? "outline"}
          className={cn(
            buttonVariants({ variant: null }),
            asyncSelectTriggerVariants({ variant: triggerVariant }),
            "flex w-full min-w-0 justify-between gap-2 text-left",
            multiple && !selectionControls
              ? "h-auto min-h-9 py-1.5"
              : "overflow-hidden",
            disabled && "opacity-50 cursor-not-allowed",
            ariaInvalid &&
              "border-destructive ring-destructive/20 dark:ring-destructive/40 ring-[3px]",
            triggerClassName,
          )}
          style={{ width: width, minWidth: width }}
          disabled={disabled}
        >
          {renderTriggerContent()}
          {multiple && clearable && selectedValues.length > 0 && !disabled && (
            <span
              role="button"
              tabIndex={0}
              aria-label={resolvedClearAriaLabel}
              className="shrink-0 rounded-sm opacity-60 hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-[2px] focus-visible:ring-ring"
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.stopPropagation();
                (onChange as (value: string[]) => void)([]);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  (onChange as (value: string[]) => void)([]);
                }
              }}
            >
              <X className="size-4" />
            </span>
          )}
          <TriggerIcon className="size-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        ref={contentRef}
        id={optionsId}
        className={cn(
          asyncSelectContentVariants({ width: contentWidth }),
          className,
        )}
        align="start"
        collisionPadding={8}
        onOpenAutoFocus={forceOpen ? (event) => event.preventDefault() : undefined}
      >
        <Command shouldFilter={false} className="min-h-0">
          <div className="relative w-full shrink-0 border-b">
            <CommandInput
              placeholder={
                label
                  ? t("placeholders.searchEntity", {
                    entity: label.toLowerCase(),
                  })
                  : t("placeholders.searchDefault")
              }
              clearable={!isSearchLoading}
              value={searchTerm}
              onValueChange={(value) => {
                setSearchTerm(value);
                setClearFailed(false);
              }}
            />
            {isSearchLoading && (
              <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            )}
          </div>
          {isPaginated ? (
            <div
              ref={listScrollRef}
              onScroll={handleListScroll}
              onWheel={(e) => e.stopPropagation()}
              onTouchMove={(e) => e.stopPropagation()}
              className="min-h-0 flex-1 max-h-[300px] overflow-y-auto overflow-x-hidden overscroll-contain scroll-py-1"
            >
              <CommandList className="max-h-none overflow-visible">
                {optionListBody}
                {hasMore && loadingMore && (
                  <div className="flex items-center justify-center py-2">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  </div>
                )}
              </CommandList>
            </div>
          ) : (
            // overscroll-contain + stopPropagation keep wheel/touch scrolling on the list itself
            // instead of a modal dialog's scroll-lock swallowing it.
            <CommandList
              className="min-h-0 flex-1 overscroll-contain"
              onWheel={(e) => e.stopPropagation()}
              onTouchMove={(e) => e.stopPropagation()}
            >
              {optionListBody}
            </CommandList>
          )}
          {selectionControls && (
            <div
              className="flex shrink-0 items-center justify-end gap-2 border-t px-2 py-1"
              onKeyDown={(e) => e.stopPropagation()}
            >
              <p role="status" className="mr-auto text-sm text-destructive">
                {clearFailed && t("errors.fetchOptionsFailed")}
              </p>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                loading={isClearing}
                aria-busy={isClearing}
                disabled={clearAllDisabled}
                onClick={handleClearAll}
              >
                {t("common.clearAll")}
              </Button>
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function DefaultLoadingSkeleton() {
  return (
    <CommandGroup>
      {[1, 2, 3].map((i) => (
        <CommandItem key={i} disabled>
          <div className="flex items-center gap-2 w-full">
            <div className="h-6 w-6 rounded-full animate-pulse bg-muted" />
            <div className="flex flex-col flex-1 gap-1">
              <div className="h-4 w-24 animate-pulse bg-muted rounded" />
              <div className="h-3 w-16 animate-pulse bg-muted rounded" />
            </div>
          </div>
        </CommandItem>
      ))}
    </CommandGroup>
  );
}
